const INSTALLATION_ID_COOKIE = 'baba_iid'
const AUTH_TOKEN_COOKIE = 'baba_jwt'
const AUTH_EMAIL_COOKIE = 'baba_email'
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365
const AUTH_COOKIE_SECONDS = 60 * 60 * 24 * 14
// Remember the email far longer than the session so an expired/returning user
// is greeted by name and routed to sign-in (not a fresh "upgrade" paywall).
const AUTH_EMAIL_COOKIE_SECONDS = ONE_YEAR_SECONDS

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'))
  return match ? decodeURIComponent(match[1]) : null
}

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
  if (typeof document === 'undefined') return
  const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : ''
  const host = typeof location !== 'undefined' ? location.hostname.toLowerCase() : ''
  // Share auth across app/www/e subdomains in production.
  const domain = host === 'itsbaba.com' || host.endsWith('.itsbaba.com') ? '; Domain=.itsbaba.com' : ''
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax${secure}${domain}`
}

function deleteCookie(name: string): void {
  if (typeof document === 'undefined') return
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax; Domain=.itsbaba.com`
}

function uuidv4(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export function getInstallationId(): string {
  const existing = readCookie(INSTALLATION_ID_COOKIE)
  if (existing) return existing
  const fresh = uuidv4()
  writeCookie(INSTALLATION_ID_COOKIE, fresh, ONE_YEAR_SECONDS)
  return fresh
}

export function getAuthToken(): string | null {
  return readCookie(AUTH_TOKEN_COOKIE)
}

/** Fired on every session change. ChatPage and the chat sync engine listen for it. */
export const AUTH_CHANGED_EVENT = 'baba:auth-changed'

function notifyAuthChanged(): void {
  try {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new Event(AUTH_CHANGED_EVENT))
    }
  } catch {}
}

export function setAuthToken(token: string, email?: string): void {
  writeCookie(AUTH_TOKEN_COOKIE, token, AUTH_COOKIE_SECONDS)
  writeCookie('baba_ext_session', token, AUTH_COOKIE_SECONDS)
  if (email) {
    writeCookie(AUTH_EMAIL_COOKIE, email, AUTH_EMAIL_COOKIE_SECONDS)
    writeCookie('baba_ext_email', email, AUTH_EMAIL_COOKIE_SECONDS)
  }
  notifyAuthChanged()
}

/**
 * A JWT's claims, decoded WITHOUT verifying the signature. For deciding what
 * to show only (whose account a sign-in link is for): every server that
 * receives the token verifies it. Null for anything that is not a readable
 * token.
 */
export function readJwtClaims(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const binary = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0))
    const claims: unknown = JSON.parse(new TextDecoder().decode(bytes))
    return claims && typeof claims === 'object' && !Array.isArray(claims) ? (claims as Record<string, unknown>) : null
  } catch {
    return null
  }
}

export function getAuthEmail(): string | null {
  return readCookie(AUTH_EMAIL_COOKIE)
}

// keepEmail distinguishes an involuntary session expiry (token rejected — keep
// the email so we recognize the returning user) from a deliberate sign-out
// (clear everything). Without this, a lapsed Pro user looks like a brand-new
// free visitor and gets shown the upgrade paywall instead of "welcome back".
export function clearAuthToken(opts?: { keepEmail?: boolean }): void {
  deleteCookie(AUTH_TOKEN_COOKIE)
  deleteCookie('baba_ext_session')
  if (!opts?.keepEmail) deleteCookie('baba_ext_email')
  if (!opts?.keepEmail) deleteCookie(AUTH_EMAIL_COOKIE)
  notifyAuthChanged()
}

export function isSignedIn(): boolean {
  return !!getAuthToken()
}
